import { Link } from 'react-router-dom';

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://everythingautomation.ai';

const termsPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Terms of Service | Everything Automation',
  description: 'Read the Everything Automation terms of service. Understand the rules and conditions governing use of our AI automation services.',
  url: `${siteUrl}/terms-of-service`,
  publisher: {
    '@type': 'Organization',
    name: 'Everything Automation',
    url: siteUrl,
  },
  dateModified: '2026-03-18',
};

const sections = [
  {
    id: 'agreement',
    title: '1. Agreement to Terms',
    content: `By accessing our website at everythingautomation.co or using any services provided by Everything Automation, you agree to be bound by these Terms of Service and our Privacy Policy.

If you do not agree to these terms, please do not use our website or services. These terms apply to all visitors, clients, and anyone who accesses or uses our services.

Everything Automation is a UK-based company operating at:
Email: sales@everythingautomation.co
Phone: +44 7450 946360`,
  },
  {
    id: 'services',
    title: '2. Our Services',
    content: `Everything Automation provides AI-powered business automation services, including but not limited to:

- AI Chatbot design, development, and deployment
- Booking and appointment automation systems
- CRM integration and configuration
- Lead capture and management automation
- Email marketing automation
- WhatsApp Business automation
- Custom AI workflows and integrations
- Website design and development (as an add-on service)

The specific services, deliverables, timelines, and pricing for each client engagement are agreed upon separately via a service agreement or proposal document prior to commencement of work.`,
  },
  {
    id: 'payments',
    title: '3. Payments & Fees',
    content: `Subscription Plans
Our monthly subscription plans are billed in advance on a recurring monthly basis. Prices are as stated on our pricing page and are inclusive of VAT where applicable.

One-Time Setup
Some services may require an initial setup fee as agreed in your service proposal. This fee is due before work commences.

Late Payments
Invoices are due within 14 days of issue unless otherwise agreed. We reserve the right to suspend services for accounts with overdue balances. Late payment interest may be applied at the statutory rate under the Late Payment of Commercial Debts Act 1998.

Payment Methods
We accept payment via bank transfer or other methods specified in your invoice. All prices are listed in GBP (£).`,
  },
  {
    id: 'cancellation',
    title: '4. Cancellation & Refunds',
    content: `Monthly Subscriptions
You may cancel your subscription at any time with 30 days written notice. Your service will continue until the end of the current billing period. No refunds are issued for partial months.

Setup Fees
Setup fees are non-refundable once work has commenced, as they cover time and resources already allocated to your project.

14-Day Cooling Off
For new clients, a 14-day cooling-off period applies from the date of the first payment, in accordance with UK Consumer Contracts Regulations 2013. A full refund will be issued if requested within this period, provided no substantial work has been delivered.

To cancel, please contact us at sales@everythingautomation.co with your account details and cancellation request.`,
  },
  {
    id: 'intellectual-property',
    title: '5. Intellectual Property',
    content: `Our Content
All content on our website, including text, graphics, logos, images, and software, is the property of Everything Automation and is protected by UK and international copyright law. You may not reproduce, distribute, or create derivative works without our express written permission.

Your Content & Data
You retain ownership of any data, content, or materials you provide to us in connection with your service. By providing such materials, you grant us a limited licence to use them solely for the purpose of delivering your services.

Deliverables
Custom automation systems, chatbot configurations, and workflows built specifically for your business become your property upon full payment. We retain the right to use anonymised project details for case studies or portfolio purposes unless you request otherwise in writing.`,
  },
  {
    id: 'client-obligations',
    title: '6. Client Obligations',
    content: `To enable us to deliver services effectively, you agree to:

- Provide accurate and complete information required to set up and configure your systems
- Grant us necessary access to platforms, tools, and accounts as required for your project
- Respond to requests for information or approvals within a reasonable timeframe (typically 5 business days)
- Ensure your use of our services complies with all applicable laws and regulations
- Not use our systems for spam, harassment, illegal activity, or any purpose that violates our acceptable use standards

Delays caused by the client's failure to meet these obligations may affect project timelines. We are not liable for delays resulting from lack of client cooperation.`,
  },
  {
    id: 'limitation-liability',
    title: '7. Limitation of Liability',
    content: `To the maximum extent permitted by UK law, Everything Automation shall not be liable for:

- Indirect, incidental, or consequential losses (including loss of revenue, profits, or business opportunities)
- Data loss or corruption not caused by our direct negligence
- Service interruptions caused by third-party platforms or tools outside our control
- Any losses arising from your reliance on automated outputs without human review

Our total liability for any claim arising from our services shall not exceed the total fees paid by you in the 3 months preceding the claim.

Nothing in these terms limits liability for death or personal injury caused by our negligence, fraud, or any other liability that cannot be excluded under UK law.`,
  },
  {
    id: 'third-party-tools',
    title: '8. Third-Party Tools & Platforms',
    content: `Our services may involve integration with or use of third-party platforms such as CRM systems, calendar tools, WhatsApp Business API, email providers, and others.

We are not responsible for:
- Changes to third-party platform policies, APIs, or pricing
- Downtime or technical issues caused by third-party platforms
- Actions taken by third-party platforms that affect your automation systems

Where third-party costs are incurred on your behalf (e.g. WhatsApp API charges), these will be communicated to you in advance and billed accordingly.`,
  },
  {
    id: 'confidentiality',
    title: '9. Confidentiality',
    content: `Both parties agree to keep confidential any proprietary or sensitive information shared during the course of the engagement. This includes but is not limited to business strategies, client lists, pricing structures, and technical systems.

This obligation survives the termination of services. Neither party shall disclose confidential information to third parties without prior written consent, except where required by law.`,
  },
  {
    id: 'termination',
    title: '10. Termination',
    content: `We reserve the right to terminate or suspend your access to our services immediately and without notice if:

- You breach any of these Terms of Service
- Payment remains overdue for more than 30 days
- You use our services for unlawful or abusive purposes
- We reasonably believe continuation of the service poses a legal or reputational risk

Upon termination, you remain liable for any outstanding balances. We will provide reasonable access to export your data within 30 days of termination.`,
  },
  {
    id: 'governing-law',
    title: '11. Governing Law',
    content: `These Terms of Service are governed by and construed in accordance with the laws of England and Wales. Any disputes arising from these terms or your use of our services shall be subject to the exclusive jurisdiction of the courts of England and Wales.

If any provision of these terms is found to be unenforceable, the remaining provisions will continue in full force and effect.`,
  },
  {
    id: 'changes',
    title: '12. Changes to These Terms',
    content: `We may update these Terms of Service from time to time. We will notify existing clients of material changes via email. The latest version will always be available on this page. Continued use of our services after changes are posted constitutes acceptance of the updated terms.`,
  },
  {
    id: 'contact',
    title: '13. Contact Us',
    content: `For any questions regarding these Terms of Service, please contact us:

Email: sales@everythingautomation.co
Phone: +44 7450 946360
WhatsApp: +44 7450 946360

We aim to respond to all legal queries within 5 business days.`,
  },
];

const TermsPage = () => {
  return (
    <>
      <title>Terms of Service | Everything Automation</title>
      <meta name="description" content="Read the Everything Automation terms of service. These terms govern your use of our website and AI automation services. Last updated March 2026." />
      <meta name="keywords" content="Everything Automation terms of service, AI automation terms, service agreement UK" />
      <link rel="canonical" href={`${siteUrl}/terms-of-service`} />
      <meta property="og:title" content="Terms of Service | Everything Automation" />
      <meta property="og:description" content="The rules and conditions governing use of Everything Automation website and AI automation services." />
      <meta property="og:url" content={`${siteUrl}/terms-of-service`} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(termsPageSchema) }} />
      <main className="bg-ea-black min-h-screen">
        {/* Hero */}
        <section className="relative pt-32 pb-16 overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-b from-ea-blue/5 to-transparent"></div>
          </div>
          <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-ea-blue/10 border border-ea-blue/30 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-ea-blue"></span>
              <span className="text-ea-blue text-xs font-medium uppercase tracking-wider">Legal</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
              Terms of Service
            </h1>
            <p className="text-ea-gray text-base leading-relaxed max-w-2xl">
              Please read these terms carefully before using our website or engaging our services. They set out the rules governing our relationship with you.
            </p>
            <p className="text-ea-gray/60 text-sm mt-4">
              Last updated: <span className="text-ea-gray">18 March 2026</span>
            </p>
          </div>
        </section>

        {/* Content */}
        <section className="py-12 pb-24">
          <div className="max-w-4xl mx-auto px-6 lg:px-12">
            {/* Quick Nav */}
            <div className="bg-ea-dark-card border border-ea-dark-border rounded-xl p-6 mb-10">
              <p className="text-white font-semibold text-sm mb-4 uppercase tracking-wider">Contents</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {sections.map((s) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="text-ea-gray hover:text-ea-blue text-sm transition-colors duration-200 cursor-pointer flex items-center gap-2"
                    rel="nofollow"
                  >
                    <i className="ri-arrow-right-s-line text-ea-blue text-xs"></i>
                    {s.title}
                  </a>
                ))}
              </div>
            </div>

            {/* Disclaimer banner */}
            <div className="bg-ea-blue/10 border border-ea-blue/30 rounded-xl p-5 mb-10 flex items-start gap-4">
              <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-information-line text-ea-blue text-lg"></i>
              </div>
              <p className="text-ea-gray text-sm leading-relaxed">
                By using our website or services, you agree to these terms. If you have any questions, please{' '}
                <Link to="/contact" className="text-ea-blue hover:underline cursor-pointer">contact us</Link>{' '}
                before proceeding.
              </p>
            </div>

            {/* Sections */}
            <div className="space-y-10">
              {sections.map((s) => (
                <div
                  key={s.id}
                  id={s.id}
                  className="bg-ea-dark-card border border-ea-dark-border rounded-xl p-8 scroll-mt-24"
                >
                  <h4 className="text-white font-bold text-lg mb-5 flex items-center gap-3">
                    <span className="w-1 h-5 bg-ea-blue rounded-full inline-block shrink-0"></span>
                    <a id={s.id} className="hover:text-ea-blue transition-colors">{s.title}</a>
                  </h4>
                  <div className="text-ea-gray text-sm leading-relaxed whitespace-pre-line">
                    {s.content}
                  </div>
                </div>
              ))}
            </div>

            {/* Back & Contact */}
            <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4 pt-8 border-t border-ea-dark-border">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-ea-gray hover:text-white text-sm transition-colors duration-200 cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Home
              </Link>
              <div className="flex items-center gap-4">
                <Link
                  to="/privacy-policy"
                  className="text-ea-gray hover:text-ea-blue text-sm transition-colors duration-200 cursor-pointer"
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-ea-blue hover:bg-ea-blue-light text-white font-semibold text-sm rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Questions? Contact Us <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default TermsPage;
